import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api, ApiError, can,
  type Stock, type OutNow, type Catalogue, type StockItem, type OpenLoan,
  type ShelfRow,
} from '../lib/api'
import { CollectionRun } from './CollectionRun'

/** Equipment: what the organization owns, and where every piece of it is.
 *
 *  PLAN.html §07. Two flows sharing one spine — an item left on scene, attached
 *  to the ADDRESS so anyone can be sent to collect it; and an item rented to a
 *  PERSON against a deposit, with a numbered receipt.
 *
 *  OUT NOW COMES FIRST, AND IT DOES NOT NAG
 *
 *  §07 is explicit about two stages of visibility. Everything out is always
 *  listed; only a loan whose reminder date has arrived is highlighted. A loan
 *  with no date set never prompts — the dispatcher chose that, and inventing a
 *  default for him is the escalation bug again.
 *
 *  THE SERVER DECIDES WHAT IS DUE
 *
 *  `due` arrives on the row. Recomputing it here would let the count above the
 *  list disagree with the list, which is exactly what happened to the
 *  escalation queue.
 *
 *  A COLLECTION IS A CHECKLIST, AND WHAT IS NOT TICKED STAYS OUT
 *
 *  After completing a run the response names what is still there, and it is
 *  shown. "The volunteer isn't going to wait around, so a missed item must be
 *  noticed by the system, not by memory." */

const STATE_LABEL: Record<string, string> = {
  available: 'In stock',
  out: 'Out',
  in_transit: 'Coming back',
  repair: 'Repair',
  lost: 'Lost',
}

function when(value: string | null): string {
  if (!value) return ''
  return new Date(value).toLocaleString([], {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
}

export function Equipment() {
  const [stock, setStock] = useState<Stock | null>(null)
  const [out, setOut] = useState<OutNow | null>(null)
  const [cat, setCat] = useState<Catalogue | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [releasing, setReleasing] = useState<StockItem | null>(null)
  const [returning, setReturning] = useState<OpenLoan | null>(null)
  // Two different acts behind one location picker -- see `WhereDialog`. The
  // act is decided by the row that opened it and carried here, never inferred
  // from the item's state inside the dialog.
  const [moving, setMoving] =
    useState<{ item: StockItem; kind: 'move' | 'restock' } | null>(null)
  const [collecting, setCollecting] = useState<OpenLoan | null>(null)
  const [tab, setTab] = useState<'out' | 'stock' | 'catalogue'>('out')
  /** The asset tag of the row currently writing, or null.
   *
   *  ONE ROW ACTION AT A TIME, because these three write straight from
   *  `onClick` with no dialog in between. Two quick presses on "Send for
   *  repair" fire two POSTs before `load()` re-renders the row away, and
   *  `_place` writes a second `equipment_movements` row for a hop that never
   *  happened -- and that table is what answers "does he already have a pump
   *  with him". Every dialog in this file already has a `busy`; these had
   *  nothing. */
  const [acting, setActing] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([api.stock(), api.outNow()])
      setStock(s)
      setOut(o)
      if (can('settings.equipment')) setCat(await api.catalogue())
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load equipment.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The sentence is worked out in ONE place and also HANDED BACK, because the
  // banner it writes to sits behind a modal backdrop. A dialog that reports a
  // refusal only through here leaves the dispatcher looking at an un-spun
  // button and silence until he cancels, so it echoes the same string inside
  // itself rather than deciding on a second wording of its own.
  function fail(e: unknown, fallback: string): string {
    const sentence = e instanceof ApiError ? e.message : fallback
    setError(sentence)
    setNote(null)
    return sentence
  }

  /** Runs one row's write with everything else in that column locked out. */
  async function rowAction(id: string, run: () => Promise<void>) {
    if (acting) return
    setActing(id)
    try {
      await run()
    } finally {
      setActing(null)
    }
  }

  // Two shapes reach here -- the catalogue's one row per location, and the
  // stock view's one row per location AND type. Normalised to {id, name} so
  // the picker does not have to know which it was handed.
  const places: { id: string; name: string }[] = cat
    ? cat.locations.filter((l) => l.active).map((l) => ({ id: l.id, name: l.name }))
    : Array.from(new Map((stock?.locations ?? []).map(
        (l) => [l.location_id, { id: l.location_id, name: l.location_name }],
      )).values())

  // The collection dialog is handed the shelf rows THEMSELVES, not the pairs
  // above, because that is the contract it was written to. Deduped by
  // location: `stock_by_location` returns one row per location AND type, so a
  // warehouse holding four kinds of thing would otherwise appear four times in
  // a list whose whole job is "somewhere for this to come back to". Nothing is
  // missing from it -- that query selects from `locations` and every active
  // one is in there, standard load or not.
  const shelves: ShelfRow[] = Array.from(new Map(
    (stock?.locations ?? []).map((l) => [l.location_id, l]),
  ).values())

  return (
    <div className="workspace pad">
      {error && <div className="notice">{error}</div>}
      {note && <div className="notice ok">{note}</div>}

      <div className="tabs">
        <button className={tab === 'out' ? 'on' : ''} onClick={() => setTab('out')}>
          Out now {out && out.loans.length > 0 && (
            <span className="pill">{out.loans.length}</span>
          )}
          {out && out.due > 0 && <span className="tag due">{out.due} due</span>}
        </button>
        <button className={tab === 'stock' ? 'on' : ''} onClick={() => setTab('stock')}>
          Inventory
        </button>
        {can('settings.equipment') && (
          <button className={tab === 'catalogue' ? 'on' : ''}
                  onClick={() => setTab('catalogue')}>
            Catalogue
          </button>
        )}
      </div>

      {tab === 'out' && out && (
        <>
          <p className="blurb">
            Everything not in the organization’s hands. This list is always
            here and does not nag — an item only starts prompting once the
            reminder date set when it went out has arrived. A loan with no date
            stays visible and stays quiet.
          </p>
          {out.loans.length === 0 ? (
            <div className="listempty">Nothing is out.</div>
          ) : out.loans.map((l) => (
            <div className={`listrow ${l.due ? 'due' : ''}`} key={l.id}>
              <span className="mono">{l.asset_tag}</span>
              <span className="nm">{l.type_name}</span>
              {l.flow === 'on_scene' ? (
                <span className="chans">{l.place_label ?? 'an address'}</span>
              ) : (
                <span className="chans">
                  {l.contact_name ?? l.borrower_name ?? 'a person'}
                  {l.receipt_number && (
                    <span className="mono"> · {l.receipt_number}</span>
                  )}
                </span>
              )}
              {l.incident_number && <span className="tag">{l.incident_number}</span>}
              {l.due && <span className="tag due">due</span>}
              <span className="when">
                out {l.days_out === 0 ? 'today' : `${l.days_out}d`}
              </span>
              {can('equipment.mark_returned') && (
                <button className="ghost compact" onClick={() => setReturning(l)}>
                  Mark back
                </button>
              )}
              {/* COLLECTION IS PER ADDRESS, NOT PER ROW. §07 attaches an item
                  left on scene to the ADDRESS rather than to the member who
                  dropped it, precisely so anyone can be sent to fetch it — and
                  what he is sent with is a checklist of everything still at
                  that address, not of the one line somebody happened to click.
                  So this opens the run for the PLACE. A rental has no address
                  to send anybody to, which is why the button is not on those
                  rows; and `place_id` is checked rather than assumed, because
                  the type allows a null even though `release` refuses one. */}
              {l.flow === 'on_scene' && l.place_id
                && can('equipment.mark_returned') && (
                <button className="ghost compact"
                        onClick={() => setCollecting(l)}>
                  Collect from here
                </button>
              )}
            </div>
          ))}
          {out.loans.some((l) => l.deposit_taken !== null) && (
            <p className="footnote">
              A deposit is released when the item is marked back. Where a
              deposit was overridden, both the preset and what was actually
              taken are kept on the record, with who changed it.
            </p>
          )}
        </>
      )}

      {tab === 'stock' && stock && (
        <>
          <h3 className="rolesub">Where everything is</h3>
          <p className="blurb">
            A vehicle is a stockroom that drives. Every item is somewhere, so
            “where is PMP-03” has an answer at every point.
          </p>
          <div className="tablewrap">
            <table className="report">
              <thead><tr>
                <th>Asset</th><th>Type</th><th>State</th><th>Where</th><th></th>
              </tr></thead>
              <tbody>
                {stock.items.map((i) => (
                  <tr key={i.id}>
                    <td className="mono">{i.asset_tag}</td>
                    <td>{i.type_name}</td>
                    <td>
                      <span className={`tag ${i.state}`}>
                        {STATE_LABEL[i.state] ?? i.state}
                      </span>
                    </td>
                    <td>
                      {i.location_name ?? i.place_label ?? i.borrower_name ?? '—'}
                      {i.released_at && (
                        <span className="when"> since {when(i.released_at)}</span>
                      )}
                    </td>
                    <td>
                      {i.state === 'available' && can('equipment.release') && (
                        <button className="ghost compact"
                                onClick={() => setReleasing(i)}>
                          Send out
                        </button>
                      )}
                      {i.state === 'in_transit' && can('equipment.mark_returned')
                        && places.length > 0 && (
                        <button
                          className="ghost compact"
                          disabled={acting !== null}
                          onClick={() => rowAction(i.id, async () => {
                            try {
                              await api.confirmArrival(i.id, {
                                location_id: places[0].id,
                              })
                              setNote(`${i.asset_tag} is back in stock.`)
                              await load()
                            } catch (e) { fail(e, 'Could not confirm.') }
                          })}>
                          Confirm arrival
                        </button>
                      )}
                      {/* MOVING IS OFFERED EXACTLY WHEN THE ITEM IS SOMEWHERE,
                          and the row already says whether it is. Migration 031
                          makes `location_id IS NULL` hold for exactly `out`,
                          `in_transit` and `lost` — which are exactly the three
                          states `equipment.move` refuses — so a row with a
                          location name is one the server will accept and a row
                          without is a button that could only ever come back as
                          "mark it back first". And with nowhere else to put it
                          there is no move to make: a picker whose only option
                          is the shelf the item is already on is a dialog with
                          one wrong answer in it. */}
                      {i.location_name && can('equipment.move')
                        && places.some((p) => p.id !== i.location_id) && (
                        <button className="ghost compact"
                                onClick={() =>
                                  setMoving({ item: i, kind: 'move' })}>
                          Move
                        </button>
                      )}
                      {i.state === 'available' && can('equipment.condition') && (
                        <button className="ghost compact"
                                disabled={acting !== null}
                                onClick={() => rowAction(i.id, async () => {
                          try {
                            // NO LOCATION IS SENT, and that is the point. The
                            // server falls back to the one the item already
                            // has, and an item in for repair has not gone
                            // anywhere — it is on the same shelf with a label
                            // on it. Passing `places[0]` here to satisfy the
                            // "not lost means somewhere" rule would teleport it
                            // to whichever warehouse happened to sort first.
                            await api.setItemState(i.id, { state: 'repair' })
                            setNote(`${i.asset_tag} is in for repair, so it is `
                              + `no longer offered for release.`)
                            await load()
                          } catch (e) {
                            fail(e, 'Could not send it for repair.')
                          }
                        })}>
                          Send for repair
                        </button>
                      )}
                      {(i.state === 'available' || i.state === 'repair')
                        && can('equipment.condition') && (
                        <button className="ghost compact danger"
                                disabled={acting !== null}
                                onClick={() => rowAction(i.id, async () => {
                          // WRITING AN ASSET OFF IS WORTH ONE QUESTION, and
                          // sending it for repair is not. Lost is the only
                          // condition change that leaves the item nowhere at
                          // all, and the way back out of it needs a human to
                          // say where it turned up; repair is undone from this
                          // same row with one click and no information.
                          if (!confirm(
                            `Record ${i.asset_tag} as lost? It stops being `
                            + `anywhere at all, and bringing it back needs `
                            + `somebody to say where it turned up.`)) return
                          try {
                            await api.setItemState(i.id, { state: 'lost' })
                            setNote(`${i.asset_tag} is recorded as lost.`)
                            await load()
                          } catch (e) { fail(e, 'Could not mark it lost.') }
                        })}>
                          Mark lost
                        </button>
                      )}
                      {/* THE ONLY WAY BACK. `available` is the single state the
                          server will move an item into from `repair` or
                          `lost`, so without this control anything reaching
                          either is stranded there for good. It asks where,
                          because a lost item has no location to fall back
                          to. */}
                      {(i.state === 'repair' || i.state === 'lost')
                        && can('equipment.condition') && places.length > 0 && (
                        <button className="ghost compact"
                                onClick={() =>
                                  setMoving({ item: i, kind: 'restock' })}>
                          Back in stock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="rolesub">What each van should be carrying</h3>
          <p className="footnote">
            Restocking is “what is missing from this van”, not somebody’s
            memory. A shortfall is worked out on the server so this list and any
            count above it cannot disagree.
          </p>
          <div className="tablewrap">
            <table className="report">
              <thead><tr>
                <th>Location</th><th>Type</th><th>Should carry</th><th>Has</th>
              </tr></thead>
              <tbody>
                {stock.locations.filter((r) => r.type_name).map((r) => (
                  <tr key={`${r.location_id}-${r.type_id}`}>
                    <td>{r.location_name}
                      <span className="tag">{r.kind}</span></td>
                    <td>{r.type_name}</td>
                    <td>{r.expected}</td>
                    <td className={r.present < r.expected ? 'short' : ''}>
                      {r.present}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'catalogue' && cat && (
        <Catalogue2 cat={cat} onChanged={load} onError={fail} />
      )}

      {releasing && (
        <ReleaseDialog
          item={releasing}
          onClose={() => setReleasing(null)}
          onDone={async (message) => {
            setReleasing(null); setNote(message); await load()
          }}
          onError={fail}
        />
      )}

      {returning && (
        <ReturnDialog
          loan={returning}
          locations={places}
          onClose={() => setReturning(null)}
          onDone={async (message) => {
            setReturning(null); setNote(message); await load()
          }}
          onError={fail}
        />
      )}

      {moving && (
        <WhereDialog
          item={moving.item}
          kind={moving.kind}
          locations={places}
          onClose={() => setMoving(null)}
          onDone={async (message) => {
            setMoving(null); setNote(message); await load()
          }}
          onError={fail}
        />
      )}

      {collecting && collecting.place_id && (
        <CollectionRun
          placeId={collecting.place_id}
          placeLabel={collecting.place_label ?? 'this address'}
          incidentId={collecting.incident_id}
          locations={shelves}
          onClose={() => setCollecting(null)}
          onDone={async (message) => {
            setCollecting(null); setNote(message); await load()
          }}
          onError={fail}
        />
      )}
    </div>
  )
}

function ReleaseDialog({ item, onClose, onDone, onError }: {
  item: StockItem
  onClose: () => void
  onDone: (message: string) => void
  onError: (e: unknown, fallback: string) => void
}) {
  const [flow, setFlow] = useState<'on_scene' | 'rental'>(
    item.rentable ? 'rental' : 'on_scene')
  const [placeId, setPlaceId] = useState('')
  const [borrower, setBorrower] = useState('')
  const [phone, setPhone] = useState('')
  const [deposit, setDeposit] = useState(
    item.default_deposit !== null ? String(item.default_deposit) : '')
  const [remind, setRemind] = useState('')
  // Whether the dispatcher deliberately cleared the reminder, as against never
  // having touched it. Opposite instructions; collapsing them turns a "do not
  // prompt me" back into a prompt.
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)

  /** There is a figure to override, and this role may not override it. Both
   *  halves, in the server's own order (app/equipment.py) -- no preset means
   *  nothing was overridden, whoever is typing. */
  const locked = item.default_deposit !== null && !can('equipment.override_deposit')

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Send out {item.asset_tag}</h3>
        <div className="seg">
          <button className={flow === 'on_scene' ? 'on' : ''}
                  onClick={() => setFlow('on_scene')}>Left on scene</button>
          <button className={flow === 'rental' ? 'on' : ''}
                  disabled={!item.rentable}
                  onClick={() => setFlow('rental')}>Rented out</button>
        </div>

        {flow === 'on_scene' ? (
          <>
            <label>Address
              <input value={placeId} placeholder="Paste the place id"
                     onChange={(e) => setPlaceId(e.target.value)} />
            </label>
            <p className="footnote">
              Attached to the address, not to the member who dropped it —
              anyone can be sent to collect it.
            </p>
          </>
        ) : (
          <>
            <label>Who is taking it
              <input value={borrower} autoFocus
                     onChange={(e) => setBorrower(e.target.value)} />
            </label>
            <label>Phone
              <input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            {/* LOCKED ONLY WHEN THERE IS A PRESET TO OVERRIDE, which is the
                same condition the server tests (`expected is not None` in
                app/equipment.py). It used to be locked whenever the role
                lacked the key, so for an item type with NO preset the
                dispatcher could not record cash he had just counted -- the
                release went through with no deposit, a real numbered receipt
                was printed, and the return had nothing to hand back. And the
                footnote explained the block by naming a preset that did not
                exist. */}
            <label>Deposit
              <input value={deposit} inputMode="decimal"
                     disabled={locked}
                     onChange={(e) => setDeposit(e.target.value)} />
            </label>
            {locked && (
              <p className="footnote">
                The preset is {item.default_deposit}, the organization’s
                decision. Changing it for one loan is a coordinator’s.
              </p>
            )}
          </>
        )}

        <label>Remind from
          <input type="date" value={remind}
                 onChange={(e) => { setRemind(e.target.value); setTouched(true) }} />
        </label>
        <p className="footnote">
          Leave it empty and this stays visible in “out now” without ever
          prompting. Set a date and it starts prompting from that day.
        </p>

        <div className="modalrow">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy} onClick={async () => {
            setBusy(true)
            try {
              const res = await api.release({
                item_id: item.id,
                flow,
                place_id: flow === 'on_scene' ? (placeId || null) : null,
                borrower_name: flow === 'rental' ? borrower : null,
                borrower_phone: flow === 'rental' ? (phone || null) : null,
                deposit_taken: flow === 'rental' && deposit ? deposit : null,
                remind_from: remind || null,
                remind_default: !touched,
              })
              onDone(
                res.receipt_number
                  ? `${item.asset_tag} is out. Receipt ${res.receipt_number} — `
                    + `print it and staple it to the deposit.`
                  : `${item.asset_tag} is out.`)
            } catch (e) {
              onError(e, 'Could not send it out.')
            } finally { setBusy(false) }
          }}>{busy ? 'Sending…' : 'Send out'}</button>
        </div>
      </div>
    </div>
  )
}

function ReturnDialog({ loan, locations, onClose, onDone, onError }: {
  loan: OpenLoan
  locations: { id: string; name: string }[]
  onClose: () => void
  onDone: (message: string) => void
  onError: (e: unknown, fallback: string) => void
}) {
  const [where, setWhere] = useState(locations[0]?.id ?? '')
  const [busy, setBusy] = useState(false)

  return (
    <div className="modalback" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{loan.asset_tag} is back</h3>
        <label>Where is it now
          <select value={where} onChange={(e) => setWhere(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
        {loan.deposit_taken !== null && (
          <p className="footnote">
            The {loan.deposit_taken} deposit is released when this is saved.
            {loan.deposit_overridden && ' This loan had an overridden deposit; '
              + `the preset was ${loan.deposit_expected}.`}
          </p>
        )}
        <div className="modalrow">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy} onClick={async () => {
            setBusy(true)
            try {
              const res = await api.markReturned(loan.id, { location_id: where })
              onDone(res.awaiting_depot_check
                ? `${loan.asset_tag} is on its way back. It stays in transit `
                  + `until the depot confirms it arrived.`
                : `${loan.asset_tag} is back in stock.`)
            } catch (e) {
              onError(e, 'Could not mark it back.')
            } finally { setBusy(false) }
          }}>{busy ? 'Saving…' : 'Mark back'}</button>
        </div>
      </div>
    </div>
  )
}

/** Where is it now — the one question a restock and a recovery both ask.
 *
 *  ONE PICKER, TWO ACTS, AND THEY STAY TWO
 *
 *  `equipment.move` and `equipment.condition` are separate ticks on separate
 *  routes and must remain so: §19 splits them because writing an asset off is
 *  not the same act as putting it in a different van. What they share is the
 *  last question, over the same list of locations, and a second copy of that
 *  list is a second place for the rules below to drift. So the ACT is decided
 *  by the row that opened this and handed in `kind`; nothing here infers it
 *  from the item's state.
 *
 *  A MOVE MUST LAND SOMEWHERE ELSE; A RECOVERY NEED NOT
 *
 *  Moving an item onto the shelf it is already on writes a movement row for a
 *  hop that did not happen, and `equipment_movements` is what answers "does he
 *  already have a pump with him". Putting a repaired item back where it came
 *  from is the ordinary case, so for a recovery that is the default rather
 *  than the exclusion — and a lost item has no location to default to, which
 *  is the whole reason this asks instead of assuming. */
function WhereDialog({ item, kind, locations, onClose, onDone, onError }: {
  item: StockItem
  kind: 'move' | 'restock'
  locations: { id: string; name: string }[]
  onClose: () => void
  onDone: (message: string) => void
  onError: (e: unknown, fallback: string) => string
}) {
  const targets = kind === 'move'
    ? locations.filter((l) => l.id !== item.location_id)
    : locations
  const [where, setWhere] = useState(
    (kind === 'restock' ? item.location_id : null) ?? targets[0]?.id ?? '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // THE BACKDROP NEEDS THE PRESS AND THE RELEASE. Press inside the reason
  // field, drag to select a word, release past the edge of the dialog, and the
  // browser fires `click` on the nearest common ancestor — which is the
  // backdrop. Testing the click alone would throw the dialog away for the
  // crime of selecting some text.
  const downOnBackdrop = useRef(false)

  return (
    <div
      className="modalback"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h3>
          {kind === 'move'
            ? `Move ${item.asset_tag}`
            : `${item.asset_tag} is back in stock`}
        </h3>
        {/* The screen's own banner is behind the backdrop, so a refusal shown
            only there is invisible until somebody cancels. `fail` still writes
            it — this echoes the sentence it hands back rather than inventing a
            second wording of the same failure. */}
        {err && <div className="notice" role="alert">{err}</div>}
        <label>Where is it now
          <select value={where} onChange={(e) => setWhere(e.target.value)}>
            {targets.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
        {kind === 'move' ? (
          <>
            <label>Why (optional)
              <input value={reason} placeholder="Restocking after the flood"
                     onChange={(e) => setReason(e.target.value)} />
            </label>
            <p className="footnote">
              Every hop is kept, including the dull ones — a restock from a
              warehouse into a van is exactly the move that answers whether the
              responder already heading out has a pump with him.
            </p>
          </>
        ) : (
          <p className="footnote">
            {item.state === 'lost'
              ? 'Recorded as lost, so right now it is nowhere. Saving this '
                + 'puts it back on a shelf and back in the list of things that '
                + 'can be sent out.'
              : 'In for repair. Saving this makes it available for release '
                + 'again from wherever you put it.'}
          </p>
        )}
        <div className="modalrow">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !where}
                  onClick={async () => {
            setBusy(true); setErr(null)
            try {
              if (kind === 'move') {
                const res = await api.moveItem(item.id, {
                  location_id: where, reason: reason.trim() || null,
                })
                onDone(`${item.asset_tag} is now at ${res.location}.`)
              } else {
                // `available` is the only state that brings something back
                // out of repair or lost, and the server insists on a location
                // for anything that is not lost -- an item that is not lost is
                // somewhere. The response names neither, so the landing spot
                // is read off the list that was just chosen from.
                await api.setItemState(item.id, {
                  state: 'available', location_id: where,
                })
                const landed = targets.find((l) => l.id === where)?.name
                onDone(`${item.asset_tag} is back in stock`
                  + (landed ? ` at ${landed}.` : '.'))
              }
            } catch (e) {
              setErr(onError(e, kind === 'move'
                ? 'Could not move it.'
                : 'Could not put it back in stock.'))
            } finally { setBusy(false) }
          }}>
            {busy ? 'Saving…' : (kind === 'move' ? 'Move' : 'Back in stock')}
          </button>
        </div>
      </div>
    </div>
  )
}

function Catalogue2({ cat, onChanged, onError }: {
  cat: Catalogue
  onChanged: () => Promise<void>
  onError: (e: unknown, fallback: string) => void
}) {
  const [typeName, setTypeName] = useState('')
  const [typeDeposit, setTypeDeposit] = useState('')
  const [rentable, setRentable] = useState(false)
  const [locName, setLocName] = useState('')
  const [locKind, setLocKind] = useState<'warehouse' | 'vehicle'>('warehouse')
  const [device, setDevice] = useState('')
  const [tag, setTag] = useState('')
  const [typeId, setTypeId] = useState('')
  const [where, setWhere] = useState('')

  return (
    <>
      <h3 className="rolesub">Item types</h3>
      <p className="blurb">
        The catalogue, with the preset deposit a rental is measured against.
        Changing a preset does not change a loan already out — the figure is
        frozen at release.
      </p>
      {cat.types.map((t) => (
        <div className="listrow" key={t.id}>
          <span className="nm">{t.name}</span>
          {t.rentable && <span className="tag">rentable</span>}
          {t.default_deposit !== null && (
            <span className="chans">deposit {t.default_deposit}</span>
          )}
        </div>
      ))}
      <div className="daterow">
        <label>Name <input value={typeName}
                           onChange={(e) => setTypeName(e.target.value)} /></label>
        <label>Deposit <input value={typeDeposit} inputMode="decimal"
                              onChange={(e) => setTypeDeposit(e.target.value)} /></label>
        <label><input type="checkbox" checked={rentable}
                      onChange={(e) => setRentable(e.target.checked)} /> Rentable</label>
        <button className="ghost" disabled={!typeName.trim()} onClick={async () => {
          try {
            await api.addType({
              name: typeName.trim(),
              default_deposit: typeDeposit || null,
              rentable,
            })
            setTypeName(''); setTypeDeposit(''); setRentable(false)
            await onChanged()
          } catch (e) { onError(e, 'Could not add the type.') }
        }}>Add type</button>
      </div>

      <h3 className="rolesub">Warehouses and vehicles</h3>
      <p className="footnote">
        A vehicle carries a Traccar device id so its stock can be read against
        where it actually is.
      </p>
      {cat.locations.map((l) => (
        <div className="listrow" key={l.id}>
          <span className="nm">{l.name}</span>
          <span className="tag">{l.kind}</span>
          {l.traccar_device_id && (
            <span className="mono chans">{l.traccar_device_id}</span>
          )}
        </div>
      ))}
      <div className="daterow">
        <label>Name <input value={locName}
                           onChange={(e) => setLocName(e.target.value)} /></label>
        <label>Kind
          <select value={locKind}
                  onChange={(e) => setLocKind(e.target.value as 'warehouse' | 'vehicle')}>
            <option value="warehouse">Warehouse</option>
            <option value="vehicle">Vehicle</option>
          </select>
        </label>
        {locKind === 'vehicle' && (
          <label>Traccar device <input value={device}
                                       onChange={(e) => setDevice(e.target.value)} /></label>
        )}
        <button className="ghost" disabled={!locName.trim()} onClick={async () => {
          try {
            await api.addLocation({
              name: locName.trim(), kind: locKind,
              traccar_device_id: locKind === 'vehicle' ? (device || null) : null,
            })
            setLocName(''); setDevice('')
            await onChanged()
          } catch (e) { onError(e, 'Could not add the location.') }
        }}>Add location</button>
      </div>

      <h3 className="rolesub">Add an item</h3>
      <p className="footnote">
        Tracked individually from day one, each with its own asset ID — even
        though nothing is physically tagged yet. Tags get written onto the
        equipment once this is running; building it individual now avoids a
        migration later.
      </p>
      <div className="daterow">
        <label>Asset tag <input value={tag} placeholder="PMP-03"
                                onChange={(e) => setTag(e.target.value)} /></label>
        <label>Type
          <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">Pick one</option>
            {cat.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label>Starts at
          <select value={where} onChange={(e) => setWhere(e.target.value)}>
            <option value="">Pick one</option>
            {cat.locations.filter((l) => l.active).map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
        <button className="ghost" disabled={!tag.trim() || !typeId || !where}
                onClick={async () => {
          try {
            await api.addItem({
              asset_tag: tag.trim(), type_id: typeId, location_id: where,
            })
            setTag('')
            await onChanged()
          } catch (e) { onError(e, 'Could not add the item.') }
        }}>Add item</button>
      </div>
    </>
  )
}

/** The strip that sits permanently on the dispatch screen.
 *
 *  §07: "the 'out now' panel sits permanently on the dispatch screen so anyone
 *  on duty can see something is out. IT DOES NOT NAG."
 *
 *  So it renders as one quiet line when nothing is due, and says how many are
 *  due when some are. It never opens anything by itself and never takes focus
 *  — a dispatcher mid-call is not being asked about a pump. */
export function OutNowStrip({ onOpen }: { onOpen: () => void }) {
  const [out, setOut] = useState<OutNow | null>(null)

  useEffect(() => {
    if (!can('equipment.view')) return
    let alive = true
    const load = () =>
      api.outNow().then((o) => { if (alive) setOut(o) }).catch(() => {})
    load()
    // Slower than the call queue. Nothing here is time-critical: an item that
    // has been out overnight is not more out because we asked again.
    const t = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  // Nothing out is not worth a line on a working screen.
  if (!out || out.loans.length === 0) return null

  return (
    <button className={`outstrip ${out.due ? 'due' : ''}`} onClick={onOpen}>
      <span className="pill">{out.loans.length}</span>
      {out.loans.length === 1 ? 'item is out' : 'items are out'}
      {out.due > 0 && <span className="tag due">{out.due} due back</span>}
      <span className="chans">
        {out.loans.slice(0, 3).map((l) => l.asset_tag).join(', ')}
        {out.loans.length > 3 && ` and ${out.loans.length - 3} more`}
      </span>
    </button>
  )
}
